import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, TextField, Button, IconButton, Typography, Box,
  MenuItem, Select, FormControl, InputLabel, CircularProgress,
  Divider, Alert,
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { updateUser, changeUserRole, getAvailableRoles, updateUserEmail } from '../services/adminUserService';

const FIELD_DEFS = [
  { name: 'Name',         label: 'Full Name',     required: true,  grid: 6 },
  { name: 'Email',        label: 'Email',         required: true,  grid: 6, type: 'email' },
  { name: 'PhoneNumber',  label: 'Phone Number',  required: false, grid: 6 },
  { name: 'Street',       label: 'Street',        required: false, grid: 6 },
  { name: 'StreetNumber', label: 'Street No.',    required: false, grid: 3 },
  { name: 'PostCode',     label: 'Post Code',     required: false, grid: 3 },
  { name: 'City',         label: 'City',          required: false, grid: 6 },
  { name: 'Country',      label: 'Country',       required: false, grid: 6 },
  { name: 'Notes',        label: 'Notes',         required: false, grid: 12, multiline: true, rows: 3 },
];

/**
 * EditUserDialog
 * Props:
 *   open, onClose, user (user object), onUpdated (callback)
 */
const EditUserDialog = ({ open, onClose, user, onUpdated }) => {
  const [form, setForm]       = useState({});
  const [role, setRole]       = useState('');
  const [roles, setRoles]     = useState([]);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  // Populate form when user changes
  useEffect(() => {
    if (!user) return;
    const init = {};
    FIELD_DEFS.forEach(({ name }) => { init[name] = user[name] ?? ''; });
    setForm(init);
    setRole(user.Role || user.role || user.RoleName || '');
    setError('');
  }, [user]);

  // Fetch roles once
  useEffect(() => {
    if (!open) return;
    getAvailableRoles()
      .then((res) => {
        const list = res?.payload || res?.data || res || [];
        setRoles(Array.isArray(list) ? list : []);
      })
      .catch(() => setRoles([]));
  }, [open]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSave = async () => {
    if (!form.Name?.trim()) { setError('Name is required.'); return; }
    if (!form.Email?.trim()) { setError('Email is required.'); return; }
    setError('');
    setSaving(true);
    try {
      const userId = user.Id || user.id;
      // Update user info
      await updateUser(userId, form);
      // Update role if changed
      const originalRole = user.Role || user.role || user.RoleName || '';
      if (role && role !== originalRole) {
        await changeUserRole(userId, role);
      }
      toast.success('User updated successfully');
      onUpdated?.();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Update failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>Edit User</Typography>
          {user && (
            <Typography variant="caption" color="text.secondary">
              {user.Name || user.Email}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2}>
          {FIELD_DEFS.map(({ name, label, required, grid, type, multiline, rows }) => (
            <Grid item xs={12} sm={grid} key={name}>
              <TextField
                fullWidth size="small" label={label} name={name}
                type={type || 'text'}
                required={required}
                multiline={!!multiline} rows={rows}
                value={form[name] ?? ''}
                onChange={handleChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Grid>
          ))}

          {/* Role */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={role}
                label="Role"
                onChange={(e) => setRole(e.target.value)}
                sx={{ borderRadius: 1.5 }}
              >
                {roles.map((r) => (
                  <MenuItem key={r.Id || r.Name || r} value={r.Name || r.name || r}>
                    {r.Name || r.name || r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 1.5 }}>
          Cancel
        </Button>
        <Button
          variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
          sx={{
            fontWeight: 700, textTransform: 'none', borderRadius: 1.5,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' },
          }}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;
